// frontend/src/pages/admin/User.tsx
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Button,
  HStack,
  Select,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
  Stack,
  Divider,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  NumberInput,
  NumberInputField,
  ButtonGroup,
  FormControl,
  FormLabel
} from '@chakra-ui/react'
import { SearchIcon } from '@chakra-ui/icons'
import {
  getUserList,
  adjustUserUsdt,
  adjustUserPoint,
  adminChangePassword,
  purchaseUserPlan,
  increaseService as apiIncreaseService,
  createUserByAdmin,
  adminChangeUsername
} from '../../services/apiService'
import dayjs from 'dayjs'

type UserItem = {
  id: number
  account: string
  username: string
  email: string | null
  phone: string | null
  usdtBalance: string | null
  pointBalance: string | null
  isAdmin: number
  serviceAt: string | null
  referralAt: string | null
  subscriptionAt: string | null
  createdAt: string | null
}

// 라벨/코드 분리 (서버에는 product 코드만 보냄)
const PLAN_OPTIONS = [
  { label: '체험판 (7일 제공)', value: 'DEMO' },
  { label: 'BASIC 플랜 [-200 USDT]', value: 'BASIC' },
  { label: 'VIP 플랜 [-2,400 USDT]', value: 'VIP' },
  { label: 'SECRET 플랜 [-4,000 USDT]', value: 'SECRET' },
  { label: 'SECRET+ 플랜 [-5,000 USDT]', value: 'SECRET+' }
] as const
type PlanValue = (typeof PLAN_OPTIONS)[number]['value']

const User = () => {
  const { t } = useTranslation()
  const toast = useToast()

  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<UserItem[]>([])
  const [loading, setLoading] = useState(false)

  // 액션 대상 유저
  const [selectedUser, setSelectedUser] = useState<UserItem | null>(null)

  // USDT 조정
  const adjustDisc = useDisclosure()
  const [adjustAmount, setAdjustAmount] = useState<string>('0')
  const [adjustType, setAdjustType] = useState<'credit' | 'debit'>('credit')
  const [submittingAdjust, setSubmittingAdjust] = useState(false)

  // 포인트 조정
  const adjustPointDisc = useDisclosure()
  const [adjustPointAmount, setAdjustPointAmount] = useState<string>('0')
  const [adjustPointType, setAdjustPointType] = useState<'credit' | 'debit'>('credit')
  const [submittingAdjustPoint, setSubmittingAdjustPoint] = useState(false)

  // 비밀번호 변경
  const changePasswordDisc = useDisclosure()
  const [newPassword, setNewPassword] = useState<string>('')
  const [secretKey, setSecretKey] = useState<string>('')
  const [submittingChangePassword, setSubmittingChangePassword] = useState(false)

  // 이용권 구매
  const planDisc = useDisclosure()
  const [selectedPlan, setSelectedPlan] = useState<PlanValue>('DEMO')
  const [submittingPlan, setSubmittingPlan] = useState(false)

  // ✅ 이용일 증가(관리자)
  const increaseDisc = useDisclosure()
  const [increaseMode, setIncreaseMode] = useState<'days' | 'date'>('days')
  const [increaseDays, setIncreaseDays] = useState<string>('1')
  const [targetDate, setTargetDate] = useState<string>('')
  const [nextExpiryHint, setNextExpiryHint] = useState<string>('')
  const [submittingIncrease, setSubmittingIncrease] = useState(false)

  // ✅ 회원명 변경
  const changeNameDisc = useDisclosure()
  const [newName, setNewName] = useState<string>('')
  const [submittingChangeName, setSubmittingChangeName] = useState(false)

  // 회원 추가
  const addUserDisc = useDisclosure()
  const [addAccount, setAddAccount] = useState<string>('')
  const [addPassword, setAddPassword] = useState<string>('')
  const [addUsername, setAddUsername] = useState<string>('')
  const [addPhone, setAddPhone] = useState<string>('')
  const [submittingAddUser, setSubmittingAddUser] = useState(false)

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  // 입력 변화나 선택 유저 변화 시 예상 만료일 계산
  useEffect(() => {
    if (!selectedUser) {
      setNextExpiryHint('')
      return
    }

    const now = dayjs()
    const current = selectedUser.serviceAt
      ? dayjs(selectedUser.serviceAt)
      : null
    const base = current && current.isAfter(now) ? current : now

    if (increaseMode === 'days') {
      const days = Number(increaseDays)
      if (!Number.isFinite(days) || days <= 0) {
        setNextExpiryHint('')
        return
      }
      const newDate = base.add(days, 'day').format('YYYY-MM-DD HH:mm:ss')
      setNextExpiryHint(`연장 후 만료일: ${newDate}`)
    } else {
      // date mode
      if (!targetDate) {
        setNextExpiryHint('')
        return
      }
      const t = dayjs(targetDate)
      if (!t.isValid()) {
        setNextExpiryHint('유효하지 않은 날짜')
        return
      }
      setNextExpiryHint(`설정될 만료일: ${t.format('YYYY-MM-DD HH:mm:ss')}`)
    }
  }, [increaseDays, targetDate, increaseMode, selectedUser])

  const isMobile = useBreakpointValue({ base: true, md: false })

  const fetchList = async (
    override?: Partial<{ page: number; pageSize: number; keyword: string }>
  ) => {
    try {
      setLoading(true)
      const params = { page, pageSize, keyword, ...override }
      const res = await getUserList(params)
      const payload = res?.data?.data
      setItems(payload?.items ?? [])
      setTotal(payload?.total ?? 0)
      if (typeof payload?.page === 'number') setPage(payload.page)
      if (typeof payload?.pageSize === 'number') setPageSize(payload.pageSize)
    } catch (e: any) {
      toast({
        title: '회원 목록 조회 실패',
        description: e?.message ?? '네트워크 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize])

  const onSearch = () => {
    setPage(1)
    fetchList({ page: 1 })
  }

  const formatDate = (v?: string | null) => {
    if (!v) return '-'
    const d = dayjs(v)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : v
  }

  const ExpiryCell = ({ value }: { value?: string | null }) => {
    if (!value) return <Text>-</Text>
    const d = dayjs(value)
    if (!d.isValid()) return <Text>{value}</Text>
    const now = dayjs()
    const diffDays = d.endOf('day').diff(now, 'day')
    const isExpired = d.isBefore(now, 'minute')
    const label = isExpired
      ? `${Math.abs(diffDays)}일 지남`
      : `${diffDays}일 남음`
    const display = d.format('YYYY-MM-DD HH:mm:ss')
    return (
      <Tooltip label={label} openDelay={300}>
        <Text color={isExpired ? 'red.500' : undefined}>{display}</Text>
      </Tooltip>
    )
  }

  const formatUSDTInt = (v?: string | null) => {
    if (v == null || v === '') return '-'
    const intPart = String(v).split('.')[0] || '0'
    const num = Number(intPart.replace(/[^0-9-]/g, ''))
    if (Number.isNaN(num)) return intPart
    return num.toLocaleString()
  }

  // === 모달 열기 ===
  const openAdjustModal = (user: UserItem) => {
    setSelectedUser(user)
    setAdjustAmount('0')
    setAdjustType('credit')
    adjustDisc.onOpen()
  }

  const openAdjustPointModal = (user: UserItem) => {
    setSelectedUser(user)
    setAdjustPointAmount('0')
    setAdjustPointType('credit')
    adjustPointDisc.onOpen()
  }

  const openChangePasswordModal = (user: UserItem) => {
    setSelectedUser(user)
    setNewPassword('')
    setSecretKey('')
    changePasswordDisc.onOpen()
  }

  const openPlanModal = (user: UserItem) => {
    setSelectedUser(user)
    setSelectedPlan('DEMO')
    planDisc.onOpen()
  }

  const openIncreaseModal = (user: UserItem) => {
    setSelectedUser(user)
    setIncreaseMode('days')
    setIncreaseDays('1')
    setTargetDate('')
    increaseDisc.onOpen()
  }

  const openChangeNameModal = (user: UserItem) => {
    setSelectedUser(user)
    setNewName(user.username)
    changeNameDisc.onOpen()
  }

  // === 제출: USDT 조정 ===
  const submitAdjust = async () => {
    if (!selectedUser) return
    const raw = Number(adjustAmount)
    if (Number.isNaN(raw)) {
      toast({ title: '숫자를 입력하세요.', status: 'warning' })
      return
    }
    const signedAmount = adjustType === 'credit' ? raw : -raw
    if (signedAmount === 0) {
      toast({ title: '0은 입력할 수 없습니다.', status: 'warning' })
      return
    }

    try {
      setSubmittingAdjust(true)
      const { data: res } = await adjustUserUsdt({
        userId: selectedUser.id,
        amount: signedAmount
      })
      if (res.statusCode === 200) {
        toast({
          title: 'USDT 조정 완료',
          description: res.message,
          status: 'success'
        })
        adjustDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: 'USDT 조정 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingAdjust(false)
    }
  }

  // === 제출: 포인트 조정 ===
  const submitAdjustPoint = async () => {
    if (!selectedUser) return
    const raw = Number(adjustPointAmount)
    if (Number.isNaN(raw)) {
      toast({ title: '숫자를 입력하세요.', status: 'warning' })
      return
    }
    const signedAmount = adjustPointType === 'credit' ? raw : -raw
    if (signedAmount === 0) {
      toast({ title: '0은 입력할 수 없습니다.', status: 'warning' })
      return
    }

    try {
      setSubmittingAdjustPoint(true)
      const { data: res } = await adjustUserPoint({
        userId: selectedUser.id,
        amount: signedAmount
      })
      if (res.statusCode === 200) {
        toast({
          title: '포인트 조정 완료',
          description: res.message,
          status: 'success'
        })
        adjustPointDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '포인트 조정 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingAdjustPoint(false)
    }
  }

  // === 제출: 비밀번호 변경 ===
  const submitChangePassword = async () => {
    if (!selectedUser) return
    
    if (!newPassword.trim()) {
      toast({ title: '새 비밀번호를 입력하세요.', status: 'warning' })
      return
    }
    
    if (newPassword.length < 4) {
      toast({ title: '비밀번호는 4글자 이상이어야 합니다.', status: 'warning' })
      return
    }
    
    if (!secretKey.trim()) {
      toast({ title: '비밀키를 입력하세요.', status: 'warning' })
      return
    }

    try {
      setSubmittingChangePassword(true)
      const { data: res } = await adminChangePassword({
        userId: selectedUser.id,
        newPassword: newPassword.trim(),
        secretKey: secretKey.trim()
      })
      if (res.statusCode === 200) {
        toast({
          title: '비밀번호 변경 완료',
          description: res.message,
          status: 'success'
        })
        changePasswordDisc.onClose()
        setNewPassword('')
        setSecretKey('')
      }
    } catch (e: any) {
      toast({
        title: '비밀번호 변경 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingChangePassword(false)
    }
  }

  // === 제출: 이용권 구매 ===
  const submitPlan = async () => {
    if (!selectedUser) return
    try {
      setSubmittingPlan(true)
      const { data: res } = await purchaseUserPlan({
        userId: selectedUser.id,
        product: selectedPlan
      })
      if (res.statusCode === 200) {
        toast({ title: '이용권 구매 완료', status: 'success' })
        planDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '이용권 구매 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingPlan(false)
    }
  }

  // === 제출: 회원 추가 ===
  const submitAddUser = async () => {
    if (!addAccount.trim() || !addPassword.trim() || !addUsername.trim() || !addPhone.trim()) {
      toast({ title: '모든 필드를 입력하세요.', status: 'warning' })
      return
    }

    if (addPassword.length < 4) {
      toast({ title: '비밀번호는 4자 이상이어야 합니다.', status: 'warning' })
      return
    }

    try {
      setSubmittingAddUser(true)
      const { data: res } = await createUserByAdmin({
        account: addAccount.trim(),
        password: addPassword.trim(),
        username: addUsername.trim(),
        phone: addPhone.trim()
      })
      if (res.statusCode === 200) {
        toast({
          title: '회원 추가 완료',
          description: res.message,
          status: 'success'
        })
        addUserDisc.onClose()
        setAddAccount('')
        setAddPassword('')
        setAddUsername('')
        setAddPhone('')
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '회원 추가 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingAddUser(false)
    }
  }

  // ✅ 제출: 이용일 증가(관리자)
  const submitIncrease = async () => {
    if (!selectedUser) return

    let payload: any = { userId: selectedUser.id }

    if (increaseMode === 'days') {
      const days = Number(increaseDays)
      if (!Number.isFinite(days) || days <= 0) {
        toast({ title: '증가 일수를 올바르게 입력하세요.', status: 'warning' })
        return
      }
      payload.addDay = days
    } else {
      if (!targetDate) {
        toast({ title: '날짜를 선택하세요.', status: 'warning' })
        return
      }
      // 날짜 포맷 확인 (YYYY-MM-DD HH:mm:ss 권장)
      const d = dayjs(targetDate)
      if (!d.isValid()) {
        toast({ title: '유효하지 않은 날짜입니다.', status: 'warning' })
        return
      }
      payload.targetDate = d.format('YYYY-MM-DD HH:mm:ss')
    }

    try {
      setSubmittingIncrease(true)
      const { data: res } = await apiIncreaseService(payload)
      if (res.statusCode === 200) {
        toast({
          title: '이용일 설정 완료',
          description: res.message,
          status: 'success'
        })
        increaseDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '이용일 설정 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingIncrease(false)
    }
  }

  // ✅ 제출: 회원명 변경
  const submitChangeName = async () => {
    if (!selectedUser) return
    if (!newName.trim()) {
      toast({ title: '이름을 입력하세요.', status: 'warning' })
      return
    }

    try {
      setSubmittingChangeName(true)
      const { data: res } = await adminChangeUsername({
        userId: selectedUser.id,
        newUsername: newName.trim()
      })
      if (res.statusCode === 200) {
        toast({
          title: '이름 변경 완료',
          description: res.message,
          status: 'success'
        })
        changeNameDisc.onClose()
        fetchList()
      }
    } catch (e: any) {
      toast({
        title: '이름 변경 실패',
        description:
          e?.response?.data?.message ?? '처리 중 오류가 발생했습니다.',
        status: 'error'
      })
    } finally {
      setSubmittingChangeName(false)
    }
  }

  // === 모바일 카드 UI ===
  const MobileCard = ({ u }: { u: UserItem }) => (
    <Box borderWidth="1px" borderRadius="lg" p={4}>
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontWeight="bold">{u.username}</Text>
        {u.isAdmin ? (
          <Badge colorScheme="purple" variant="subtle">
            관리자
          </Badge>
        ) : (
          <Badge colorScheme="gray" variant="subtle">
            일반
          </Badge>
        )}
      </Flex>
      <Text fontSize="sm" color="gray.600">
        @{u.account}
      </Text>
      <Divider my={3} />

      <Stack spacing={1} fontSize="sm">
        <Flex justify="space-between">
          <Text color="gray.500">연락처</Text>
          <Text>{u.phone ?? '-'}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">보유 USDT</Text>
          <Text fontWeight="semibold">{formatUSDTInt(u.usdtBalance)}</Text>
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">보유 포인트</Text>
          <Text fontWeight="semibold" color="blue.600">{formatUSDTInt(u.pointBalance)} P</Text>
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">서비스일</Text>
          <ExpiryCell value={u.serviceAt} />
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">레퍼럴</Text>
          <ExpiryCell value={u.referralAt} />
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">구독료</Text>
          <ExpiryCell value={u.subscriptionAt} />
        </Flex>
        <Flex justify="space-between">
          <Text color="gray.500">생성일</Text>
          <Text>{formatDate(u.createdAt)}</Text>
        </Flex>
      </Stack>

      <HStack mt={3} spacing={2} wrap="wrap">
        <Button size="sm" variant="outline" onClick={() => openAdjustModal(u)}>
          USDT 조정
        </Button>
        <Button size="sm" variant="outline" colorScheme="blue" onClick={() => openAdjustPointModal(u)}>
          포인트 조정
        </Button>
        <Button size="sm" variant="outline" colorScheme="red" onClick={() => openChangePasswordModal(u)}>
          비밀번호변경
        </Button>
        <Button size="sm" variant="outline" colorScheme="orange" onClick={() => openChangeNameModal(u)}>
          이름변경
        </Button>
        <Button size="sm" onClick={() => openPlanModal(u)}>
          이용권 구매
        </Button>
        {/* ✅ 이용일 증가 버튼 */}
        <Button
          size="sm"
          colorScheme="teal"
          onClick={() => openIncreaseModal(u)}
        >
          이용일 설정
        </Button>
      </HStack>
    </Box>
  )

  return (
    <DefaultLayout>
      <AdminNav />

      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* 검색/필터 */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          mb={3}
          gap={3}
        >
          <Heading as="h2" size="md">
            회원 관리
          </Heading>

          <HStack spacing={3} align="stretch">
            <Button colorScheme="green" onClick={addUserDisc.onOpen}>
              회원 추가
            </Button>
            <InputGroup width={{ base: '100%', md: '280px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} />
              </InputLeftElement>
              <Input
                placeholder="아이디/이름/연락처"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              />
            </InputGroup>
            <Button onClick={onSearch} minW={{ base: 'auto', md: '70px' }}>
              검색
            </Button>
            <Select
              width={{ base: '110px', md: '120px' }}
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                setPageSize(next)
                setPage(1)
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}개씩
                </option>
              ))}
            </Select>
          </HStack>
        </Flex>

        {/* 총 건수/페이지 */}
        <Flex justify="space-between" align="center" mb={2}>
          <Text color="gray.600" fontSize="sm">
            총 <b>{total.toLocaleString()}</b>명
          </Text>
          <Text color="gray.500" fontSize="sm">
            페이지 {page} / {totalPages}
          </Text>
        </Flex>

        {/* 리스트 */}
        {loading ? (
          <Flex align="center" justify="center" py={8} gap={2}>
            <Spinner />
            <Text>불러오는 중…</Text>
          </Flex>
        ) : items.length === 0 ? (
          <Flex align="center" justify="center" py={8}>
            <Text color="gray.500">데이터가 없습니다.</Text>
          </Flex>
        ) : isMobile ? (
          <Stack spacing={3}>
            {items.map((u) => (
              <MobileCard key={u.id} u={u} />
            ))}
          </Stack>
        ) : (
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>번호</Th>
                  <Th>아이디</Th>
                  <Th>이름</Th>
                  <Th>연락처</Th>
                  <Th isNumeric>보유 USDT</Th>
                  <Th isNumeric>보유 포인트</Th>
                  <Th>권한</Th>
                  <Th>서비스일</Th>
                  <Th>레퍼럴</Th>
                  <Th>구독료</Th>
                  <Th>생성일</Th>
                  <Th>액션</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((u) => (
                  <Tr key={u.id}>
                    <Td>{u.id}</Td>
                    <Td>{u.account}</Td>
                    <Td>{u.username}</Td>
                    <Td>{u.phone ?? '-'}</Td>
                    <Td isNumeric>{formatUSDTInt(u.usdtBalance)}</Td>
                    <Td isNumeric>
                      <Text color="blue.600">{formatUSDTInt(u.pointBalance)} P</Text>
                    </Td>
                    <Td>
                      {u.isAdmin ? (
                        <Badge colorScheme="purple" variant="subtle">
                          관리자
                        </Badge>
                      ) : (
                        <Badge colorScheme="gray" variant="subtle">
                          일반
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      <ExpiryCell value={u.serviceAt} />
                    </Td>
                    <Td>
                      <ExpiryCell value={u.referralAt} />
                    </Td>
                    <Td>
                      <ExpiryCell value={u.subscriptionAt} />
                    </Td>
                    <Td>{formatDate(u.createdAt)}</Td>
                    <Td>
                      <HStack spacing={2} wrap="wrap">
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => openAdjustModal(u)}
                        >
                          USDT 조정
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => openAdjustPointModal(u)}
                        >
                          포인트 조정
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="red"
                          onClick={() => openChangePasswordModal(u)}
                        >
                          비밀번호변경
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="orange"
                          onClick={() => openChangeNameModal(u)}
                        >
                          이름변경
                        </Button>
                        <Button size="xs" onClick={() => openPlanModal(u)}>
                          이용권 구매
                        </Button>
                        {/* ✅ 이용일 증가 버튼 */}
                        <Button
                          size="xs"
                          colorScheme="teal"
                          onClick={() => openIncreaseModal(u)}
                        >
                          이용일 설정
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* 페이지네이션 */}
        <Flex align="center" justify="center" gap={2} mt={4}>
          <Button
            onClick={() => setPage(1)}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            처음
          </Button>
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            이전
          </Button>
          <Text fontSize="sm">
            {page} / {totalPages}
          </Text>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            다음
          </Button>
          <Button
            onClick={() => setPage(totalPages)}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            마지막
          </Button>
        </Flex>
      </Box>

      {/* USDT 조정 모달 */}
      <Modal isOpen={adjustDisc.isOpen} onClose={adjustDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>USDT 지급/회수</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={2}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 지급/회수 선택 */}
            <HStack mb={3}>
              <ButtonGroup isAttached>
                <Button
                  onClick={() => setAdjustType('credit')}
                  colorScheme={adjustType === 'credit' ? 'blue' : undefined}
                >
                  지급
                </Button>
                <Button
                  onClick={() => setAdjustType('debit')}
                  colorScheme={adjustType === 'debit' ? 'red' : undefined}
                >
                  회수
                </Button>
              </ButtonGroup>
            </HStack>

            {/* ✅ 퀵 버튼 */}
            <HStack mb={3} spacing={2}>
              {[200, 2400, 4000, 5000].map((amt) => (
                <Button
                  key={amt}
                  size="sm"
                  variant="outline"
                  onClick={() => setAdjustAmount(String(amt))}
                >
                  {amt.toLocaleString()}
                </Button>
              ))}
            </HStack>

            {/* 금액 입력 */}
            <NumberInput
              value={adjustAmount}
              onChange={(v) => setAdjustAmount(v)}
              min={0}
            >
              <NumberInputField placeholder="금액 입력 (정수/소수 가능)" />
            </NumberInput>

            <Text mt={2} fontSize="xs" color="gray.500">
              지급은 +, 회수는 -로 처리됩니다. (회수 시 음수로 변환)
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={adjustDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingAdjust}
              onClick={submitAdjust}
              colorScheme={adjustType === 'credit' ? 'blue' : 'red'}
            >
              적용
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 이용권 구매 모달 */}
      <Modal isOpen={planDisc.isOpen} onClose={planDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>이용권 구매</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={2}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>
            <Select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value as PlanValue)}
            >
              {PLAN_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>

            <Box mt={3}>
              <Text fontSize={'sm'}>
                사용자의 USDT를 차감하고 이용권을 구매합니다.
              </Text>
              <Text fontSize={'sm'}>
                따라서, 오프라인 매출의 경우 USDT 지급 후 해당 기능을
                사용해야합니다.
              </Text>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={planDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingPlan}
              onClick={submitPlan}
              colorScheme="blue"
            >
              구매
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 이용일 증가 모달 */}
      <Modal
        isOpen={increaseDisc.isOpen}
        onClose={increaseDisc.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>이용일 증가 (관리자)</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={2}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 모드 선택 */}
            <HStack mb={4}>
              <ButtonGroup isAttached size="sm" width="100%">
                <Button
                  flex={1}
                  onClick={() => setIncreaseMode('days')}
                  colorScheme={increaseMode === 'days' ? 'teal' : undefined}
                >
                  일수 추가
                </Button>
                <Button
                  flex={1}
                  onClick={() => setIncreaseMode('date')}
                  colorScheme={increaseMode === 'date' ? 'teal' : undefined}
                >
                  날짜 지정
                </Button>
              </ButtonGroup>
            </HStack>

            {increaseMode === 'days' ? (
              <>
                {/* 퀵 버튼 */}
                <HStack mb={3} spacing={2}>
                  {[1, 3, 7, 30, 90, 365].map((d) => (
                    <Button
                      key={d}
                      size="sm"
                      variant="outline"
                      onClick={() => setIncreaseDays(String(d))}
                    >
                      +{d}일
                    </Button>
                  ))}
                </HStack>

                {/* 일수 입력 */}
                <NumberInput
                  value={increaseDays}
                  onChange={(v) => setIncreaseDays(v)}
                  min={1}
                >
                  <NumberInputField placeholder="증가 일수 (정수)" />
                </NumberInput>
              </>
            ) : (
              <>
                <Text fontSize="sm" mb={1}>
                  종료 날짜 (YYYY-MM-DD HH:mm:ss)
                </Text>
                <Input
                  type="datetime-local"
                  value={targetDate}
                  onChange={(e) => setTargetDate(e.target.value)}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  예: 2025-12-31 23:59:59
                </Text>
              </>
            )}

            {/* ✅ 연장 후 예상 만료일 표시 */}
            {nextExpiryHint && (
              <Text mt={3} fontSize="md" fontWeight="bold" color="blue.600">
                {nextExpiryHint}
              </Text>
            )}

            <Text mt={2} fontSize="xs" color="gray.500">
              만료된 경우 오늘 기준으로, 유효한 경우 기존 만료일 기준으로
              연장됩니다.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={increaseDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingIncrease}
              onClick={submitIncrease}
              colorScheme="teal"
            >
              적용
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 포인트 조정 모달 */}
      <Modal isOpen={adjustPointDisc.isOpen} onClose={adjustPointDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>포인트 지급/회수</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={2}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 지급/회수 선택 */}
            <HStack mb={3}>
              <ButtonGroup isAttached>
                <Button
                  onClick={() => setAdjustPointType('credit')}
                  colorScheme={adjustPointType === 'credit' ? 'blue' : undefined}
                >
                  지급
                </Button>
                <Button
                  onClick={() => setAdjustPointType('debit')}
                  colorScheme={adjustPointType === 'debit' ? 'red' : undefined}
                >
                  회수
                </Button>
              </ButtonGroup>
            </HStack>

            {/* 퀵 버튼 */}
            <HStack mb={3} spacing={2}>
              {[100, 500, 1000, 5000].map((amt) => (
                <Button
                  key={amt}
                  size="sm"
                  variant="outline"
                  onClick={() => setAdjustPointAmount(String(amt))}
                >
                  {amt.toLocaleString()}
                </Button>
              ))}
            </HStack>

            {/* 포인트 입력 */}
            <NumberInput
              value={adjustPointAmount}
              onChange={(v) => setAdjustPointAmount(v)}
              min={0}
            >
              <NumberInputField placeholder="포인트 입력 (정수/소수 가능)" />
            </NumberInput>

            <Text mt={2} fontSize="xs" color="gray.500">
              지급은 +, 회수는 -로 처리됩니다. (회수 시 음수로 변환)
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={adjustPointDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingAdjustPoint}
              onClick={submitAdjustPoint}
              colorScheme={adjustPointType === 'credit' ? 'blue' : 'red'}
            >
              적용
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 회원 추가 모달 */}
      <Modal isOpen={addUserDisc.isOpen} onClose={addUserDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>회원 추가</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">
                  아이디
                </FormLabel>
                <Input
                  value={addAccount}
                  onChange={(e) => setAddAccount(e.target.value)}
                  placeholder="아이디를 입력하세요 (5자 이상)"
                  size="sm"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">
                  비밀번호
                </FormLabel>
                <Input
                  type="password"
                  value={addPassword}
                  onChange={(e) => setAddPassword(e.target.value)}
                  placeholder="비밀번호를 입력하세요 (4자 이상)"
                  size="sm"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">
                  사용자명
                </FormLabel>
                <Input
                  value={addUsername}
                  onChange={(e) => setAddUsername(e.target.value)}
                  placeholder="사용자명을 입력하세요"
                  size="sm"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="bold">
                  연락처
                </FormLabel>
                <Input
                  value={addPhone}
                  onChange={(e) => setAddPhone(e.target.value)}
                  placeholder="연락처를 입력하세요 (예: 01012345678)"
                  size="sm"
                />
              </FormControl>

              <Text mt={2} fontSize="xs" color="gray.500">
                회원 추가 시 service_at이 2050-01-01 00:00:00으로 자동 설정됩니다.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={addUserDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingAddUser}
              onClick={submitAddUser}
              colorScheme="green"
            >
              추가
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 비밀번호 변경 모달 */}
      <Modal isOpen={changePasswordDisc.isOpen} onClose={changePasswordDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>비밀번호 변경</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            {/* 새 비밀번호 입력 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                새 비밀번호
              </FormLabel>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="새 비밀번호를 입력하세요 (4글자 이상)"
                size="sm"
              />
            </FormControl>

            {/* 비밀키 입력 */}
            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                비밀키
              </FormLabel>
              <Input
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="비밀키를 입력하세요"
                size="sm"
              />
            </FormControl>

            <Text mt={2} fontSize="xs" color="gray.500">
              비밀번호는 4글자 이상이어야 하며, 비밀키는 관리자만 알고 있는 키입니다.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={changePasswordDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingChangePassword}
              onClick={submitChangePassword}
              colorScheme="red"
            >
              변경
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* 회원명 변경 모달 */}
      <Modal isOpen={changeNameDisc.isOpen} onClose={changeNameDisc.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>회원명 변경</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              대상:{' '}
              {selectedUser
                ? `${selectedUser.username} (@${selectedUser.account})`
                : '-'}
            </Text>

            <FormControl mb={4}>
              <FormLabel fontSize="sm" fontWeight="bold">
                새 이름
              </FormLabel>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="새 이름을 입력하세요"
                size="sm"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={changeNameDisc.onClose}>
              취소
            </Button>
            <Button
              isLoading={submittingChangeName}
              onClick={submitChangeName}
              colorScheme="orange"
            >
              변경
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default User
